import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
const PRODUCT_IMAGE = 'https://uploadthingy.s3.us-west-1.amazonaws.com/f9dftiuyCXSegMt95tQFws/Mockup_%28ROMPE_LA_MATRIX%29.png';
const NAMES = ['Andrea M.', 'Ricardo X.', 'Lucía R.', 'Julián P.', 'María G.', 'Sebastián L.', 'Emily J.', 'Sophia K.', 'Liam D.', 'Chloe B.', 'Enzo R.', 'Valentina C.'];
const toastCopy = {
  es: {
    label: 'COMPRA RECIENTE',
    line3: (price: string) => `Edición digital • $${price} USD`
  },
  en: {
    label: 'RECENT PURCHASE',
    line3: (price: string) => `Digital edition • $${price} USD`
  }
};
const getRandomName = () => NAMES[Math.floor(Math.random() * NAMES.length)];
const getRandomPrice = () => (Math.random() * (23.97 - 7.97) + 7.97).toFixed(2);
const SESSION_STORAGE_KEY = 'recentPurchaseToastDismissed';
const MAX_SHOWS_PER_SESSION = 5;
export function RecentPurchaseToast() {
  const {
    language
  } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showCount, setShowCount] = useState(0);
  const [currentName, setCurrentName] = useState(getRandomName());
  const [currentPrice, setCurrentPrice] = useState(getRandomPrice());
  // Check if user dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);
  useEffect(() => {
    // Don't show if user dismissed it or reached max shows
    if (isDismissed || showCount >= MAX_SHOWS_PER_SESSION) return;
    // First appearance after 7-10 seconds
    const initialDelay = Math.random() * 3000 + 7000; // 7-10 seconds
    const initialTimer = setTimeout(() => {
      setCurrentName(getRandomName());
      setCurrentPrice(getRandomPrice());
      setIsVisible(true);
      setShowCount(prev => prev + 1);
      // Hide after 6 seconds
      const hideTimer = setTimeout(() => {
        setIsVisible(false);
      }, 6000);
      return () => clearTimeout(hideTimer);
    }, initialDelay);
    return () => clearTimeout(initialTimer);
  }, [isDismissed, showCount]);
  useEffect(() => {
    // Reappear every 8-15 seconds after first show
    if (isDismissed || showCount >= MAX_SHOWS_PER_SESSION) return;
    const interval = setInterval(() => {
      if (showCount >= MAX_SHOWS_PER_SESSION) return;
      // Generate new random data
      setCurrentName(getRandomName());
      setCurrentPrice(getRandomPrice());
      setIsVisible(true);
      setShowCount(prev => prev + 1);
      // Hide after 6 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 6000);
    }, Math.random() * 7000 + 8000); // 8-15 seconds
    return () => clearInterval(interval);
  }, [isDismissed, showCount]);
  const handleClose = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
  };
  const {
    label,
    line3
  } = toastCopy[language];
  return <AnimatePresence data-id="element-207">
      {isVisible && !isDismissed && <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 50,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-6 left-6 z-50 max-w-sm w-full sm:w-auto" style={{
      // Responsive positioning
      left: 'clamp(1rem, 5vw, 1.5rem)',
      bottom: 'clamp(1rem, 5vh, 1.5rem)',
      maxWidth: 'calc(100vw - 2rem)'
    }} data-id="element-208">
          <div className="relative bg-matrix-bg-light/95 backdrop-blur-md border border-[#00d2f7]/40 rounded-2xl shadow-2xl overflow-hidden" data-id="element-209">
            {/* Animated glow border */}
            <motion.div className="absolute inset-0 rounded-2xl pointer-events-none" animate={{
          boxShadow: ['0 0 20px rgba(0, 210, 247, 0.3)', '0 0 30px rgba(0, 210, 247, 0.5)', '0 0 20px rgba(0, 210, 247, 0.3)']
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-210" />

            <div className="relative z-10 p-4 flex items-start gap-3" data-id="element-211">
              {/* Product Image */}
              <div className="flex-shrink-0" data-id="element-212">
                <img src={PRODUCT_IMAGE} alt="Product" className="w-16 h-16 rounded-lg object-cover border border-[#00d2f7]/30 shadow-lg" data-id="element-213" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-214">
                {/* Line 1: Recent Purchase badge */}
                <div className="flex items-center gap-2 mb-1" data-id="element-215">
                  <motion.span className="w-2 h-2 rounded-full bg-green-400" animate={{
                opacity: [1, 0.5, 1],
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-216" />
                  <span className="text-white/60 text-[10px] font-semibold uppercase tracking-wider" data-id="element-217">
                    {label}
                  </span>
                </div>

                {/* Line 2: Buyer name */}
                <p className="text-white font-bold text-base mb-1 truncate" data-id="element-218">
                  {currentName}
                </p>

                {/* Line 3: Product info */}
                <p className="text-white/50 text-sm" data-id="element-219">{line3(currentPrice)}</p>
              </div>

              {/* Close button */}
              <button onClick={handleClose} className="flex-shrink-0 text-white/40 hover:text-white transition-colors p-1 -mt-1 -mr-1" aria-label="Close" data-id="element-220">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-221">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-222" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}